import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageHero } from '@/components/shared/PageHero';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { ACCOMPLISHMENT_METRICS, MONTHLY_ACCOMPLISHMENTS } from '@/data/mockData';
import { Users, FileCheck, Megaphone, CheckCircle2, Hammer, CalendarCheck } from 'lucide-react';

const ICONS = { Users, FileCheck, Megaphone, CheckCircle2, Hammer, CalendarCheck };

const Accomplishments = () => {
  return (
    <div data-testid="accomplishments-page">
      <PageHero
        eyebrow="Transparency"
        title="Accomplishments at a Glance"
        subtitle="A monthly look at what the barangay has delivered. Numbers we are proud of, and progress that fuels the work ahead."
        breadcrumbs={[{ label: 'Accomplishments' }]}
      />

      {/* METRICS */}
      <section className="container-app -mt-12 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {ACCOMPLISHMENT_METRICS.map((m) => {
            const Icon = ICONS[m.icon] || Users;
            return (
              <Card key={m.label} className="p-5 text-center border-0 bg-card shadow-md-soft">
                <div className="mx-auto inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="mt-3 font-display text-2xl font-bold gradient-text">{m.value.toLocaleString()}+</div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">{m.label}</div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* MONTHLY */}
      <section className="container-app section-padding">
        <SectionHeader
          eyebrow="Monthly Reports"
          title="Month-by-month accomplishments"
          description="Highlights from the past few months — published openly so every resident can follow our work."
        />

        <div className="space-y-10">
          {MONTHLY_ACCOMPLISHMENTS.map((m, idx) => (
            <Card key={m.id} className="overflow-hidden card-elevated border-0" data-testid={`accomplishment-${m.id}`}>
              <div className="grid md:grid-cols-5">
                <div className={`md:col-span-2 aspect-[4/3] md:aspect-auto bg-secondary overflow-hidden ${idx % 2 === 1 ? 'md:order-2' : ''}`}>
                  <img src={m.image} alt={m.month} className="h-full w-full object-cover" />
                </div>
                <div className="md:col-span-3 p-6 md:p-8">
                  <Badge className="bg-accent/15 text-accent hover:bg-accent/25 border-accent/30">{m.month}</Badge>
                  <h3 className="mt-3 font-display text-2xl font-bold text-primary-deep">{m.month} Highlights</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{m.summary}</p>
                  <div className="mt-5 grid sm:grid-cols-2 gap-3">
                    {m.metrics.map((mm, i) => (
                      <div key={i} className="rounded-xl border bg-secondary/40 p-3">
                        <div className="font-display text-xl font-bold text-primary">{mm.value.toLocaleString()}</div>
                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{mm.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Accomplishments;
